// @ts-check
import React, { useMemo, useState, useEffect } from "react";
import { v4 as uuid } from "uuid";
import {
  selectPollByID,
  useHMSActions,
  useHMSStore,
} from "@100mslive/react-sdk";
import { AddCircleIcon } from "@100mslive/react-icons";
import { Button, Flex, Text } from "@100mslive/roomkit-react";
import { Container, ContentHeader } from "../../Streaming/Common";
import { isValidQuestion, QuestionForm } from "./QuestionForm";
import { SavedQuestion } from "./SavedQuestion";
import { useWidgetToggle } from "../../AppData/useSidepane";
import { useWidgetState, useSetAppDataByKey } from "../../AppData/useUISettings";
import { WIDGET_VIEWS, WIDGET_STATE, APP_DATA } from "../../../common/constants";
import { saveTemplate, pollToTemplate } from "../utils/templateStorage";

export function CreateQuestions() {
  const [widgetState] = useSetAppDataByKey(APP_DATA.widgetState);
  const templateQuestions = widgetState?.templateQuestions;
  
  const initializeQuestions = () => {
    if (templateQuestions && Array.isArray(templateQuestions) && templateQuestions.length > 0) {
      // Convert template questions to draft format
      return templateQuestions.map(q => ({
        draftID: uuid(),
        text: q.text,
        type: q.type,
        options: q.options || [],
        skippable: q.skippable !== undefined ? q.skippable : true,
        saved: true, // Mark as saved so they display properly
      }));
    }
    return [{ draftID: uuid() }];
  };

  const [questions, setQuestions] = useState(initializeQuestions);
  const [saveTemplateError, setSaveTemplateError] = useState("");
  const [saveTemplateSuccess, setSaveTemplateSuccess] = useState(false);
  const actions = useHMSActions();
  const toggleWidget = useWidgetToggle();
  const { pollInView: id, setWidgetView, setWidgetState } = useWidgetState();
  const interaction = useHMSStore(selectPollByID(id));

  // Clear templateQuestions after loading
  useEffect(() => {
    if (templateQuestions && id) {
      setWidgetState({
        [WIDGET_STATE.pollInView]: id,
        [WIDGET_STATE.view]: WIDGET_VIEWS.CREATE_QUESTIONS,
        templateQuestions: undefined,
      });
    }
  }, [templateQuestions, id, setWidgetState]);

  const isValidPoll = useMemo(
    () =>
      questions.length > 0 &&
      questions.every(question => isValidQuestion(question)),
    [questions]
  );

  const launchPoll = async () => {
    const validQuestions = questions
      .filter(question => isValidQuestion(question))
      .map(question => ({
        text: question.text,
        type: question.type,
        options: question.options,
        skippable: question.skippable,
      }));
    await actions.interactivityCenter.addQuestionsToPoll(id, validQuestions);
    await actions.interactivityCenter.startPoll(id);
    setWidgetView(WIDGET_VIEWS.VOTE);
  };

  const handleSaveAsTemplate = () => {
    try {
      if (!interaction) {
        setSaveTemplateError("Poll/Quiz information not available");
        return;
      }

      const validQuestions = questions
        .filter(question => isValidQuestion(question))
        .map(question => ({
          text: question.text,
          type: question.type,
          options: question.options || [],
          skippable: question.skippable !== undefined ? question.skippable : true,
        }));

      if (validQuestions.length === 0) {
        setSaveTemplateError("Please add at least one valid question before saving as template");
        return;
      }

      const template = pollToTemplate(interaction, validQuestions);
      // Generate a unique ID for the template to avoid conflicts
      template.id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
      saveTemplate(template);
      
      setSaveTemplateError("");
      setSaveTemplateSuccess(true);
      setTimeout(() => setSaveTemplateSuccess(false), 3000);
    } catch (error) {
      console.error("Error saving template:", error);
      setSaveTemplateError("Failed to save template: " + error.message);
      setSaveTemplateSuccess(false);
    }
  };
  const headingTitle = interaction?.type
    ? interaction?.type?.[0]?.toUpperCase() + interaction?.type?.slice(1)
    : "Polls and Quizzes";
  const isQuiz = interaction?.type === "quiz";
  return (
    <Container rounded>
      <ContentHeader
        content={headingTitle}
        onClose={toggleWidget}
        onBack={() => setWidgetView(WIDGET_VIEWS.CREATE_POLL_QUIZ)}
      />
      <Flex direction="column" css={{ p: "$10" }}>
        <Flex direction="column">
          {questions.map((question, index) => (
            <QuestionCard
              key={question.draftID}
              question={question}
              index={index}
              length={questions.length}
              onSave={questionParams => {
                setQuestions(questions => [
                  ...questions.slice(0, index),
                  questionParams,
                  ...questions.slice(index + 1),
                ]);
              }}
              isQuiz={isQuiz}
              removeQuestion={questionID =>
                setQuestions(prev => {
                  return prev.filter(
                    questionFromSet => questionID !== questionFromSet.draftID
                  );
                })
              }
              convertToDraft={questionID =>
                setQuestions(prev => {
                  const copyOfQuestions = [...prev];
                  copyOfQuestions.forEach(question => {
                    if (questionID && question.draftID === questionID) {
                      question.saved = false;
                    }
                  });
                  return copyOfQuestions;
                })
              }
            />
          ))}
        </Flex>
        <Flex
          css={{
            c: "$on_surface_low",
            my: "$sm",
            cursor: "pointer",
            "&:hover": { c: "$on_surface_medium" },
          }}
          onClick={() => setQuestions([...questions, { draftID: uuid() }])}
        >
          <AddCircleIcon />
          <Text variant="body1" css={{ ml: "$md", c: "$inherit" }}>
            Add another question
          </Text>
        </Flex>
        <Flex css={{ w: "100%", gap: "$4" }} justify="end">
          <Button
            variant="standard"
            disabled={!isValidPoll}
            onClick={handleSaveAsTemplate}
          >
            Save as Template
          </Button>
          <Button disabled={!isValidPoll} onClick={launchPoll}>
            Launch {interaction.type}
          </Button>
        </Flex>
        {saveTemplateError && (
          <Text
            variant="caption"
            css={{
              c: "$alert_error_default",
              mt: "$4",
              textAlign: "right",
            }}
          >
            {saveTemplateError}
          </Text>
        )}
        {saveTemplateSuccess && (
          <Text
            variant="caption"
            css={{
              c: "$alert_success",
              mt: "$4",
              textAlign: "right",
            }}
          >
            Template saved successfully!
          </Text>
        )}
      </Flex>
    </Container>
  );
}

const QuestionCard = ({
  question,
  onSave,
  index,
  length,
  removeQuestion,
  isQuiz,
  convertToDraft,
}) => {
  return (
    <Flex
      direction="column"
      css={{ p: "$md", bg: "$surface_default", r: "$1", mb: "$sm" }}
    >
      {question.saved ? (
        <SavedQuestion
          question={question}
          index={index}
          length={length}
          convertToDraft={convertToDraft}
        />
      ) : (
        <QuestionForm
          question={question}
          removeQuestion={() => removeQuestion(question.draftID)}
          onSave={params => onSave(params)}
          index={index}
          length={length}
          isQuiz={isQuiz}
        />
      )}
    </Flex>
  );
};
